/* eslint-disable react-hooks/exhaustive-deps */
import { RoleFunction } from '@eagle/common';
import { AlertResponse, getThingFeatureCombinations, InstallationAndDevice, LastThingEvent, Thing, ThingDataSubscriptionInfo, ThingLifeCycleStateResponse, ThingType } from '@eagle/core-data-types';
import { FeatureTypes } from '@eagle/data-function-types';
import {
  ALERTS_DAYS_LIMIT,
  AlertsTable,
  CacheDataTypes,
  ContractStatusAlert,
  ContractStatusCard,
  DetailPage,
  DynamicIcon,
  evaluate,
  FeatureIds,
  FetchOneOfAll,
  FindItemsDeferredPaginatedResponse,
  FlexBox,
  FormatTimestamp,
  getLastThingLocation,
  getListResultDescription,
  getThingEventServerTime,
  Icon,
  LastContact,
  LastThingEvents,
  LastThingStateProvider,
  MediaCard,
  MediaListPageType,
  Pagination,
  Portals,
  Query,
  RECENT_EVENT_DATA_MAX_DAYS_FLAG,
  ServiceHistoryCard,
  T_MANY,
  T_ONE,
  ThingInlayMap,
  ThingPersonCard,
  TRACK_THING_DETAIL_DEFAULT_TEMPLATE,
  Undefinable,
  useAuthenticated,
  useBoolFlag,
  useCustomRoutes,
  useFetchOneCache,
  useHasAuthorization,
  useLastThingState,
  useNumberFlag,
  usePromise,
  useThingDataSubscriptionInfoSingle,
  useThingFaultSummaryQuery,
  useUiTemplate
} from '@eagle/react-common';
import { Breadcrumbs, Card, CardContent, styled, Typography } from '@ethings/ui-toolkit';
import { Box, Link } from '@mui/material';
import Axios from 'axios';
import { DateTime } from 'luxon';
import { FC, useCallback, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { useHref, useNavigate, useParams } from 'react-router-dom';
import { buildTripsCoreHistoryRange, HISTORY_CUSTOM_DATE_RANGE_FLAG, resolveTripHistoryMaxRangeHours, TRIPS_CORE_EXPERIENCE_FLAG } from '../history/history-date-range.utils';
import { DataDrivenThingEventsV2 } from './cards/data-driven-thing-event-v2';
import { DataDrivenThingEvents } from './cards/data-driven-thing-events';
import { ThingEvents } from './cards/thing-event';
import { ThingDetailProps, ThingMediaProps } from './thing-detail.types';

const StyledCard = styled(Card)({
  '& .MuiPaper-root': {
    boxShadow: 'none',
  },
});

const MEDIA_DAYS_LIMIT = 30;
const MEDIA_LIMIT = 6;
const DATA_VIEWER_ROLES = [RoleFunction.DATA_VIEWER] as const;
const CAMERA_ROLES = [RoleFunction.MEDIA_VIEWER] as const;
const ALERT_ROLES = [RoleFunction.ALERT_VIEWER] as const;
const SERVICE_ROLES = [RoleFunction.SERVICE_VIEWER, RoleFunction.SERVICE_ADMINISTRATOR] as const;
export const EVENT_DATA_MAX_DAYS_FLAG = 'track-thing-detail-events-card-days-back-feature';

export const thingDetailItemRenderer = (entity: Thing, thingType: ThingType, portal: Portals, groupIds: string[], noGroupsText: string, isClickable: boolean, personThingCard: JSX.Element, contractStatusCard: JSX.Element | null, thingInlayMap: JSX.Element, thingMedia: JSX.Element, thingAlerts: JSX.Element, thingEvents: JSX.Element, thingServiceHistory: JSX.Element, template: unknown, lastThingEvents: Undefinable<LastThingEvents>, lifeCycleState?: ThingLifeCycleStateResponse, lastDataSubscription?: ThingDataSubscriptionInfo, currentDeviceInstallations: InstallationAndDevice[] | null = null, faults?: ReturnType<typeof useThingFaultSummaryQuery>['data']): JSX.Element => {
  return <>{evaluate(template, { entity, thingType, portal, groupIds, noGroupsText, isClickable, personThingCard, contractStatusCard, thingInlayMap, thingMedia, thingAlerts, thingEvents, thingServiceHistory, lastThingEvents, lifeCycleState, lastDataSubscription, currentDeviceInstallations, faults })}</>;
};

const ThingAlerts: FC<{ thing: Thing }> = ({ thing }): JSX.Element => {
  const { restClient } = useAuthenticated();
  const { t } = useTranslation(['common', 'terms', 'track']);
  const [isRecentAlertsView, setIsRecentAlertsView] = useState(true);
  const maxDays = useNumberFlag(RECENT_EVENT_DATA_MAX_DAYS_FLAG) || ALERTS_DAYS_LIMIT;

  const handleQueryChanged = useCallback((_: Query, pagination: Pagination, isDaysLimit: boolean): FindItemsDeferredPaginatedResponse<AlertResponse> => {
    const cancelToken = Axios.CancelToken.source();
    // TODO TP-5859 update axios, use AbortController instead of deprecated CancelToken
    const startDate = DateTime.now().minus(isDaysLimit ? { days: maxDays } : { years: 3 });
    return {
      cancel: () => cancelToken.cancel(),
      promise: restClient.alert.getAlertsByThingId(thing._id, {
        dateRangeStart: startDate.toUTC().toJSDate(),
        limit: pagination.limit,
        skip: pagination.skip,
        sort: JSON.stringify({ occurred: -1 }),
      }, { cancelToken: cancelToken.token }),
    };
  }, [restClient]);

  return (
    <StyledCard data-testid="recent-alerts-card">
      <AlertsTable
        onQueryChanged={handleQueryChanged}
        renderTitle={(alerts, matchCount) => (
          <CardContent>
            {
              isRecentAlertsView ?
                <Typography variant="h5" color="text.primary" mb={2}>{t('common:component.alert-table.labels.recent-alert')}
                  <Typography component="span" variant="body2" color="text.secondary" fontStyle="italic" display="block">
                    {!alerts.length ?
                      t('common:component.media-data.hint.displaying-last', { count: maxDays }) :
                      t('common:component.alert-table.hint.results', { count: matchCount, dayCount: maxDays, entity: t('common:terms.alert', { count: matchCount }) })}
                  </Typography>
                </Typography>
                :
                <Typography variant="h5" color="text.primary" mb={2}> {t('common:terms.alert', { count: T_MANY })}
                  <Typography component="span" variant="body2" color="text.secondary" fontStyle="italic" display="block">
                    {getListResultDescription({ count: matchCount, entityKey: 'common:terms.alert', t })}
                  </Typography>
                </Typography>
            }
          </CardContent>
        )}
        showThing={false}
        smallTable={true}
        showGroups={false}
        showFeature={false}
        updateIsRecentAlertsView={setIsRecentAlertsView}
        isRecentAlertsView={isRecentAlertsView}
      />
    </StyledCard>
  );
};

export const ThingMedia: FC<ThingMediaProps> = ({ cameraFeatures, thing, thingType }) => (
  <MediaCard
    displayOnCard
    entity={thing}
    entityTypeFeatures={thingType.features}
    features={cameraFeatures}
    filterMediaFeature
    filters={{
      thingId: thing._id,
      featureTypeId: FeatureTypes.CAMERA_V0,
    }}
    pageType={MediaListPageType.THINGS}
    sort="-start"
    url="/api/v2/media-data"
    mediaDaysLimit={MEDIA_DAYS_LIMIT}
    limit={MEDIA_LIMIT}
  />
);

export const ThingDetail: FC<ThingDetailProps> = (props) => {
  return (
    <LastThingStateProvider>
      <ThingDetailInner {...props} />
    </LastThingStateProvider>
  );
};

const ThingDetailInner: FC<ThingDetailProps> = ({ thing = undefined, useDataDrivenEventList = false }) => {
  const { things: customRoutesThings, history: customRoutesHistory } = useCustomRoutes();
  const { restClient, userInfo } = useAuthenticated();
  const { t } = useTranslation(['common', 'terms', 'track', 'manage']);
  const params = useParams();
  const thingId = thing?._id || params.thingId;
  const [dataSubscriptionInfo] = useThingDataSubscriptionInfoSingle(thingId);
  const navigate = useNavigate();
  const flagThingHistory = useBoolFlag('track-thing-history-feature');
  const flagEventCard = useBoolFlag('track-thing-detail-events-card-feature');
  const flagServicingModule = useBoolFlag('portals-servicing-module-feature');
  const flagTripsCoreExperience = useBoolFlag(TRIPS_CORE_EXPERIENCE_FLAG);
  const maxHistoryCustomDateRangeHours = useNumberFlag(HISTORY_CUSTOM_DATE_RANGE_FLAG);
  const flagDataDrivenThingEventsV2 = useBoolFlag('track-event-card-v2-data-driven-feature-temporary');
  const { hasAuthorization } = useHasAuthorization();

  const [fetchState] = useLastThingState();
  const { template } = useUiTemplate('track-thing-detail', TRACK_THING_DETAIL_DEFAULT_TEMPLATE);
  const { data: faultsData } = useThingFaultSummaryQuery({ thingId: thingId ?? '' }, { enabled: Boolean(thingId) });
  const alertPermissions = hasAuthorization(ALERT_ROLES);
  const cameraPermission = hasAuthorization(CAMERA_ROLES);
  const mapPermission = hasAuthorization(DATA_VIEWER_ROLES);
  const eventsPermission = hasAuthorization(DATA_VIEWER_ROLES) && flagEventCard;
  const servicePermissions = hasAuthorization(SERVICE_ROLES);

  const href = useHref(`/${customRoutesThings}`);
  const things = useFetchOneCache(CacheDataTypes.THING);
  const isServiceHistoryVisible = flagServicingModule && servicePermissions && thingId;

  const loadData = useCallback(
    () => {
      if (!thing) return things.one<Thing>(thingId);
      return Promise.resolve(thing);
    },
    [thingId, userInfo.accountId]
  );

  const [lastThingLocation] = usePromise<Undefinable<LastThingEvent>>(
    async () => {
      if (!thingId) throw Error(t('common:common.hint.last-contact-no-id', { entity: t('terms:thing', { count: T_ONE }) }));
      return getLastThingLocation(restClient, thingId);
    },
    [restClient]
  );

  const [lastThingEvents, , lastThingEventsStatus] = usePromise(() => {
    if (!thingId) throw Error(t('common:common.hint.last-contact-no-id', { entity: t('terms:thing', { count: T_ONE }) }));
    return fetchState(thingId);
  }, [fetchState, thingId]);

  const [lifecycleState] = usePromise(
    async () => thingId ? restClient.thing.getCurrentLifecycleState(thingId) : null,
    [thingId, restClient]
  );

  const [currentDeviceInstallations] = usePromise<InstallationAndDevice[] | null>(
    async () => {
      if (!thingId) {
        return null;
      }

      try {
        const installations = await restClient.thing.getCurrentDeviceInstallations(thingId);
        return installations.length ? installations : null;
      }
      catch {
        return null;
      }
    },
    [thingId, restClient]
  );

  const ThingDetailInlayMap: FC<{ data: Thing; thingType: ThingType }> = ({ data, thingType }): JSX.Element => (
    <FlexBox sx={{ flexGrow: 0, height: 400 }}>{mapPermission && thingId && thingType.features.some(({ featureId }) => featureId === FeatureIds.TRACKING) && <ThingInlayMap thing={data} />}</FlexBox>
  );

  const ThingDetailMedia: FC<{ data: Thing; thingType: ThingType; cameraFeatures: string[] }> = ({ data, thingType, cameraFeatures }): JSX.Element => (
    <>{cameraPermission && <ThingMedia cameraFeatures={cameraFeatures} thing={data} thingType={thingType} />}</>
  );

  const ThingDetailAlerts: FC<{ data: Thing }> = ({ data }): JSX.Element => (
    <>{alertPermissions && <ThingAlerts thing={data} />}</>
  );

  const ThingDetailEvents: FC<{ data: Thing }> = ({ data }) => {
    if (!eventsPermission) {
      return null;
    }
    if (flagDataDrivenThingEventsV2) {
      return <DataDrivenThingEventsV2 thing={data} />;
    }
    return useDataDrivenEventList ? <DataDrivenThingEvents thing={data} /> : <ThingEvents thing={data} />;
  };

  const ThingDetailServiceHistory: FC<{ data: Thing }> = ({ data }): JSX.Element => (
    <>{isServiceHistoryVisible && data.sharedThingId && lifecycleState && <ServiceHistoryCard lifeCycleState={lifecycleState} thingId={data._id} thing={data} />}</>
  );

  interface ThingWithSubscription extends Thing {
    lastDataSubscription?: ThingDataSubscriptionInfo;
  }

  const renderPageContent = (data: ThingWithSubscription): JSX.Element => {
    const thing = data;
    if (!data) return <></>;
    return (
      <FetchOneOfAll
        id={data.thingTypeId}
        dataType={CacheDataTypes.THING_TYPE}
        renderFactory={(thingType: ThingType) => {
          const cameraFeature = thingType.features.find((feature) => feature.featureId === 'camera');
          const cameraFeatures = cameraFeature ? getThingFeatureCombinations(cameraFeature) : [];

          const personThingCard = <ThingPersonCard data-testid="thing-person-card" thing={data} portal={Portals.TRACK} />;
          const thingInlayMap = <ThingDetailInlayMap data={data} thingType={thingType} />;
          const thingMedia = <ThingDetailMedia data={data} thingType={thingType} cameraFeatures={cameraFeatures} />;
          const thingAlerts = <ThingDetailAlerts data={data} />;
          const thingEvents = <ThingDetailEvents data={data} />;
          const thingServiceHistory = <ThingDetailServiceHistory data={data} />;
          const contractStatusCard = dataSubscriptionInfo?.lastDataSubscription ? <ContractStatusCard dataSubscriptionInfo={dataSubscriptionInfo} thingTypeId={data.thingTypeId} /> : null;
          return thingDetailItemRenderer(
            thing,
            thingType,
            Portals.TRACK,
            data.groupIds,
            t('track:page.thing-detail.no-thing-group.hint'),
            false,
            personThingCard,
            contractStatusCard,
            thingInlayMap,
            thingMedia,
            thingAlerts,
            thingEvents,
            thingServiceHistory,
            template,
            lastThingEvents,
            lifecycleState ?? undefined,
            dataSubscriptionInfo,
            currentDeviceInstallations ?? null,
            faultsData,
          );
        }}
      />
    );
  };

  const viewHistory = (): void => {
    const date = lastThingLocation?.occurred ? DateTime.fromJSDate(lastThingLocation.occurred) : DateTime.now();
    const historyRangeDays = flagTripsCoreExperience ? 7 : 1;
    const maxRangeHours = resolveTripHistoryMaxRangeHours(maxHistoryCustomDateRangeHours);
    const tripsCoreRange = buildTripsCoreHistoryRange(date, maxRangeHours);

    navigate(`/${customRoutesHistory}`, {
      state: {
        dateEnd: flagTripsCoreExperience ? tripsCoreRange.dateEnd : date.plus({ days: historyRangeDays }).startOf('day').toJSDate(),
        dateStart: flagTripsCoreExperience ? tripsCoreRange.dateStart : date.startOf('day').toJSDate(),
        entityId: thingId,
        entityItemFields: {
          entity: CacheDataTypes.THING,
          entityType: CacheDataTypes.THING_TYPE,
          entityTypeKey: 'thingTypeId',
        },
        filters: [],
        focused: true,
        lastLocationUpdateDate: date.toJSDate(),
      },
    });
  };

  const actions = flagThingHistory
    ? [
      {
        label: t('track:common.action.view-history'),
        onClick: viewHistory,
        icon: <DynamicIcon icon={<Icon icon="HistoryIcon" />} />,
        testId: 'view-history-button',
      },
    ]
    : [];

  const breadcrumbs = (
    <Breadcrumbs>
      <Link
        color="inherit"
        href={href}
        underline="hover"
      >
        {t('terms:thing', { count: T_MANY })}
      </Link>
      <Typography color="text.primary" data-testid="thing-detail-subtitle-breadcrumb">{t('common:page.thing-detail.title')}</Typography>
    </Breadcrumbs>
  );

  return (
    <Box sx={{
      display: 'flex',
      flex: [1, 1],
      flexDirection: 'column',
      minWidth: 0,
    }}>
      <DetailPage<Thing>
        data-testid='thing-detail'
        actions={actions}
        breadcrumbs={breadcrumbs}
        loadData={loadData}
        pageAlert={<ContractStatusAlert dataSubscription={dataSubscriptionInfo} thingId={thingId} />}
        pageIcon={<Icon icon="ThingIcon" />}
        renderDisplay={({ display }) => display}
        renderPageContent={(data) => renderPageContent({ ...data, lastDataSubscription: dataSubscriptionInfo })}
        renderPageTitle={(data: Thing) => {
          if (lastThingEventsStatus === 'pending') return t('common:common.labels.loading');
          return <>
            {data.display}
            <Box sx={{ alignItems: 'center', display: 'flex', position: 'relative' }}>
              <LastContact
                lastThingEvent={lastThingEvents?.latest}
                sx={{ mr: 1 }}
              />
              {lastThingEvents?.latest &&
                <Typography data-ignore-title component='div' variant="caption">
                  {t('common:component.last-contact.labels.contact-time')} <FormatTimestamp format='relative' value={getThingEventServerTime(lastThingEvents.latest)} />
                </Typography>
              }
            </Box>
          </>;
        }}
      />
    </Box>
  );
};

export default ThingDetail;
